import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { getQueryKeyForUpscribe } from 'state/utils/getQueryKeyForUpscribe';
import useUpscribeRequestWithoutSession from 'state/hooks/useUpscribeRequest/useUpscribeRequestWithoutSession';
import { useCreatePerformanceTimer } from 'shared/hooks/useCreatePerformanceTimer';
import { CheckoutResponse } from './types';

export function getQueryKeyForGetCheckout(token: string, routePath: string) {
  return getQueryKeyForUpscribe({
    keys: ['checkout-item', `route:${routePath}`],
    token
  });
}

export const useGetCheckout = () => {
  const request = useUpscribeRequestWithoutSession<CheckoutResponse>();

  return ({ token }: { token: string }) =>
    request({
      method: 'GET',
      url: '/api/checkout/getCheckout',
      params: {
        token
      }
    });
};

export function useQueryGetCheckout(token: string | null) {
  const getCheckout = useGetCheckout();
  const createPerformanceTimer = useCreatePerformanceTimer();
  const { pathname: routerPathname } = useRouter();

  return useQuery({
    enabled: !!token,
    retry: (failureCount) => failureCount < 1, // count starts with 0
    queryKey: getQueryKeyForGetCheckout(token!, routerPathname),
    queryFn: async ({ signal }) => {
      const performanceTimer = createPerformanceTimer('getCheckoutTimer', {
        pathName: routerPathname
      });

      // If the fetch is aborted, log as failure.
      signal.addEventListener('abort', () =>
        performanceTimer.failure(
          signal.reason instanceof Error ? signal.reason : new Error('Request aborted')
        )
      );

      const [result] = await Promise.allSettled([getCheckout({ token: token! })]);

      if (result.status === 'fulfilled') {
        performanceTimer.success();
        return result.value;
      }

      performanceTimer.failure(result.reason as Error);
      throw result.reason;
    },
    meta: {
      showNotificationOnError: true
    }
  });
}
